"use client";

import { useContext, useEffect, useRef, useState } from "react";
import { logError } from "@/services/DatadogService";
import { useRouter, useSearchParams } from "next/navigation";

import { Auth } from "@/components/Auth";
import { AuthHeader } from "./components/AuthHeader";
import { AuthCarousel } from "./components/AuthCarousel/AuthCarousel";
import { LoginImage } from "./components/LoginImage/LoginImage";

import { getAuthCookie, getCookie, setCookie } from "@/services/CookiesService";
import { executePendingAction } from "@/services/PendingActionsService/PendingActionsService";
import { QREditorContext } from "@/contexts/QREditorContext";
import { UserContext } from "@/contexts/UserContext";

import { paths } from "@/utils/paths";

import { AuthContainerProps, AuthFormType, DOWNLOAD_QR_COOKIE, QR_REFERENCE_COOKIE } from "./types";
import {
  I_ForgotPasswordPage,
  I_LoginPage,
  I_RegisterPage,
  I_ResetPasswordPage,
  I_SetPasswordPage,
} from "@/data/types/pages";
import { AmplitudeTracker } from "@/containers/AmplitudeTracker/AmplitudeTracker";
import { amplitudeTrackUser } from "@/services/AmplitudeService/AmplitudeService";
import { PATHS } from "@/internals/constants/paths";

import { useApi } from "@/hooks/api/useApi";
import { useToast } from "@/hooks/ui";
import getProfileHandler from "@/interface/api/getProfile";
import { GetProfileResponse } from "@/interface/api/getProfile/types";

import { getItem, removeItem } from "@/hooks/browser/useLocalStorage";

import { PENDING_ACTION_FALLBACK } from "@/types/pendingActions";
import { AMPLITUDE_EVENT_SOURCE_STORAGE } from "@/internals/constants/events";
import { getItemSession, removeItemSession } from "@/hooks/browser/useSessionStorage";
import "./styles.scss";

export const AuthContainer = <T extends AuthFormType>({
  formType,
  pageData,
  pendingAction: initialPendingAction,
  amplitudeEvent,
}: AuthContainerProps<T>) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [authFormType, setAuthFormType] = useState<AuthFormType>(formType || "login");
  const [isActionSuccess, setIsActionSuccess] = useState(false);
  const [pendingAction, setPendingAction] = useState(initialPendingAction);
  const [isLoading, setIsLoading] = useState(false);
  const [eventProps, setEventProps] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {}, []);
  const qrEditorContext = useContext(QREditorContext);
  const userContext = useContext(UserContext);

  const { request: requestProfile } = useApi<GetProfileResponse>(getProfileHandler.getProfile);
  const { toast } = useToast();

  const createDownloadQrCookie = () => {
    const referenceCookie = getCookie(DOWNLOAD_QR_COOKIE);
    const downloadQrParams = searchParams.get("downloadQr");
    if (downloadQrParams) setCookie(DOWNLOAD_QR_COOKIE, downloadQrParams);

    if (referenceCookie || downloadQrParams) {
      const ref = downloadQrParams || referenceCookie;
      setCookie(QR_REFERENCE_COOKIE, ref);
    }
  };

  const hasRegisteredAmplitude = useRef(false);

  useEffect(() => {
    createDownloadQrCookie();
  }, []);

  useEffect(() => {
    const savedSource = getItemSession<string | null>(
      AMPLITUDE_EVENT_SOURCE_STORAGE.EVENT_SOURCE,
      null
    );

    if (savedSource) {
      setEventProps({ source: savedSource });
      removeItemSession(AMPLITUDE_EVENT_SOURCE_STORAGE.EVENT_SOURCE);
    }

    setIsReady(true);
  }, [authFormType]);

  useEffect(() => {
    if (!initialPendingAction) {
      const fallback = getItem(PENDING_ACTION_FALLBACK, null);
      if (fallback && fallback.type) {
        const isExpired = Date.now() - (fallback.timestamp || 0) > 300000;

        if (!isExpired) {
          setPendingAction(fallback);
        }

        removeItem(PENDING_ACTION_FALLBACK);
      }
    }
  }, [initialPendingAction]);

  useEffect(() => {
    if (!authFormType) return;

    const currentPath = window.location?.pathname || "";
    let targetPath = "";

    switch (authFormType) {
      case "login":
        targetPath = paths.login();
        break;
      case "register":
        targetPath = paths.signup();
        break;
      case "forgotPassword":
        targetPath = paths.forgotPassword();
        break;
      case "resetPassword": {
        const token = searchParams.get("token");
        if (token) {
          targetPath = `${paths.resetPassword()}?token=${token}`;
        } else {
          targetPath = paths.login();
        }
        break;
      }
      case "setPassword": {
        const token = searchParams.get("token");
        if (token) {
          targetPath = `${paths.setPassword()}?token=${token}`;
        } else {
          targetPath = paths.login();
        }
        break;
      }
      default:
        break;
    }

    if (targetPath && currentPath !== targetPath) {
      router.push(targetPath);
    }
  }, [authFormType, searchParams, router]);

  const registerUserIdInAmplitude = (): Promise<void> => {
    if (hasRegisteredAmplitude.current) return Promise.resolve();
    hasRegisteredAmplitude.current = true;
    const token = getAuthCookie();
    return requestProfile({ authorization: token })
      .then((response: { data: GetProfileResponse }) => {
        const profile = response?.data;
        if (profile?.id) {
          amplitudeTrackUser(profile.id);
        }
      })
      .catch(() => {
        hasRegisteredAmplitude.current = false;
        toast({ message: "genericError", type: "error" });
      });
  };

  const onActionSuccess = async () => {
    try {
      setIsLoading(true);
      if (authFormType === "register" || authFormType === "login") {
        if (pendingAction) {
          await registerUserIdInAmplitude();
        } else {
          setIsActionSuccess(true);
        }
      }
      if (pendingAction) {
        await executePendingAction(pendingAction, router, qrEditorContext, userContext);
        return;
      }
      router.push(PATHS.MY_QR_CODES);
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: "AuthContainer",
        action: "onActionSuccess",
      });
      setIsLoading(false);
      throw new Error("Error executing pending action");
    }
  };

  useEffect(() => {
    if (isActionSuccess) {
      registerUserIdInAmplitude();
    }
  }, [isActionSuccess]);

  return (
    <div className="auth__container">
      <div className="auth__container--form">
        <AuthHeader />

        <div className="auth__form">
          <Auth
            formType={formType}
            data={pageData}
            onChangeFormType={(formType) => setAuthFormType(formType)}
            onActionSuccess={onActionSuccess}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
          />
        </div>
      </div>

      {pageData.page === "login" ? (
        <div className="auth__container__image-container">
          <LoginImage data={pageData as I_LoginPage} />
        </div>
      ) : (
        <div className="auth__container--carousel">
          <AuthCarousel
            data={
              pageData as
                | I_RegisterPage
                | I_ForgotPasswordPage
                | I_ResetPasswordPage
                | I_SetPasswordPage
            }
          />
        </div>
      )}
      {isReady && (
        <AmplitudeTracker
          key={amplitudeEvent}
          eventName={amplitudeEvent}
          {...(eventProps !== null && { eventProperties: eventProps })}
        />
      )}
    </div>
  );
};
